import { getAccessToken, getSession, refreshSession, subscribeSession } from '@/shared/auth';
import { appConfig } from '@/shared/config';

/**
 * Живое соединение с сервером: одно на вкладку.
 *
 * Сервер сам сообщает об изменениях («на доске X переставили карточку»),
 * а экран перечитывает данные обычными запросами — так права проверяются в
 * одном месте, и кеш TanStack Query остаётся единственным источником правды.
 *
 * Протокол — JSON-сообщения (см. internal/realtime на бэкенде): первым идёт
 * `auth` с токеном доступа, затем `subscribe`/`unsubscribe` по каналам
 * `workspace:{id}`, `board:{id}`, `task:{id}`. Сервер отвечает `ready`,
 * `subscribed`, `error` и присылает `event`.
 *
 * Подписки считаются по ссылкам: две панели на один канал — одна подписка на
 * сервере; при переподключении все живые подписки восстанавливаются сами.
 */

export interface RealtimeEvent {
  channel: string;
  event: string;
  data: Record<string, unknown>;
}

export type EventHandler = (event: RealtimeEvent) => void;

export type ConnectionState = 'offline' | 'connecting' | 'online';

interface ServerMessage {
  type: string;
  channel?: string;
  event?: string;
  data?: Record<string, unknown>;
  message?: string;
}

// Пауза перед переподключением растёт с каждой неудачей: сеть упала на
// секунду — вернёмся быстро; сервер перезагружается — не долбим его.
const RECONNECT_MIN = 1000;
const RECONNECT_MAX = 15000;

// Код закрытия, которым сервер отвечает на неверный токен.
const WS_POLICY_VIOLATION = 1008;

class RealtimeClient {
  private socket: WebSocket | null = null;
  private state: ConnectionState = 'offline';
  private readonly handlers = new Map<string, Set<EventHandler>>();
  private readonly stateListeners = new Set<(state: ConnectionState) => void>();
  private attempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private authenticated = false;
  private started = false;

  /** Начать следить за сеансом: вошёл — подключаемся, вышел — закрываемся. */
  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;

    subscribeSession(() => {
      this.onSessionChange();
    });
    this.onSessionChange();

    // Вкладка вернулась из фона — проверить соединение, не дожидаясь таймера.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.socket === null) {
        this.connect();
      }
    });

    window.addEventListener('online', () => {
      if (this.socket === null) {
        this.connect();
      }
    });
  }

  getState(): ConnectionState {
    return this.state;
  }

  onStateChange(listener: (state: ConnectionState) => void): () => void {
    this.stateListeners.add(listener);

    return () => {
      this.stateListeners.delete(listener);
    };
  }

  /** Слушать канал. Возвращает отписку. */
  subscribe(channel: string, handler: EventHandler): () => void {
    let set = this.handlers.get(channel);

    if (set === undefined) {
      set = new Set();
      this.handlers.set(channel, set);
      this.send({ type: 'subscribe', channel });
    }

    set.add(handler);

    return () => {
      const current = this.handlers.get(channel);

      if (current === undefined) {
        return;
      }

      current.delete(handler);

      if (current.size === 0) {
        this.handlers.delete(channel);
        this.send({ type: 'unsubscribe', channel });
      }
    };
  }

  /** «Печатает» в канал — остальным подписчикам, себе не возвращается. */
  typing(channel: string): void {
    this.send({ type: 'typing', channel });
  }

  private onSessionChange(): void {
    const session = getSession();

    if (session.status === 'authenticated') {
      if (this.socket === null) {
        this.connect();
      } else if (this.authenticated) {
        // Токен обновился — сообщить серверу новый сеанс, чтобы «выйти» на
        // этом устройстве закрывало именно это соединение.
        this.send({ type: 'auth', token: getAccessToken() ?? '' });
      }
    } else if (session.status === 'anonymous') {
      this.disconnect();
    }
  }

  private url(): string {
    const base = appConfig.apiBaseUrl;
    const absolute = base.startsWith('http') ? new URL(base) : new URL(base, window.location.href);

    absolute.protocol = absolute.protocol === 'https:' ? 'wss:' : 'ws:';
    absolute.pathname = `${absolute.pathname.replace(/\/$/, '')}/ws`;

    return absolute.toString();
  }

  private connect(): void {
    const token = getAccessToken();

    if (token === null || this.socket !== null) {
      return;
    }

    this.clearReconnect();
    this.setState('connecting');

    const socket = new WebSocket(this.url());

    this.socket = socket;
    this.authenticated = false;

    socket.onopen = () => {
      socket.send(JSON.stringify({ type: 'auth', token: getAccessToken() ?? '' }));
    };

    socket.onmessage = (raw: MessageEvent<string>) => {
      let message: ServerMessage;

      try {
        message = JSON.parse(raw.data) as ServerMessage;
      } catch {
        return;
      }

      this.onMessage(message);
    };

    socket.onclose = (event: CloseEvent) => {
      if (this.socket !== socket) {
        return;
      }

      const wasAuthenticated = this.authenticated;

      this.socket = null;
      this.authenticated = false;
      this.setState('offline');

      // Сервер не принял токен (1008 — policy violation). Так бывает после
      // сна телефона: соединение умерло, токен в памяти за это время протух,
      // а новый появится только с первым 401 у обычного запроса. Ждать его
      // и стучаться протухшим по расписанию — зря; просим новый сразу. При
      // удаче смена сеанса подключит нас сама (onSessionChange).
      if (event.code === WS_POLICY_VIOLATION && !wasAuthenticated) {
        void refreshSession().then((ok) => {
          if (!ok) {
            this.scheduleReconnect();
          }
        });

        return;
      }

      this.scheduleReconnect();
    };

    socket.onerror = () => {
      // Следом всегда приходит onclose — переподключение планируется там.
    };
  }

  private onMessage(message: ServerMessage): void {
    switch (message.type) {
      case 'ready': {
        this.authenticated = true;
        this.attempts = 0;
        this.setState('online');

        // Восстановить все живые подписки после (пере)подключения.
        for (const channel of this.handlers.keys()) {
          this.send({ type: 'subscribe', channel });
        }

        break;
      }
      case 'event': {
        if (message.channel === undefined || message.event === undefined) {
          return;
        }

        const event: RealtimeEvent = {
          channel: message.channel,
          event: message.event,
          data: message.data ?? {},
        };

        for (const handler of this.handlers.get(message.channel) ?? []) {
          handler(event);
        }

        break;
      }
      case 'error': {
        // Отказ в подписке — не ошибка приложения: доступ могли закрыть, пока
        // вкладка была открыта. Экран узнает об этом при следующем запросе.
        if (import.meta.env.DEV) {
          console.warn('[realtime]', message.channel, message.message);
        }

        break;
      }
      default:
        break;
    }
  }

  private send(payload: Record<string, string>): void {
    if (this.socket?.readyState !== WebSocket.OPEN) {
      return;
    }

    // Подписки до `ready` не отправляются — их восстановит сам `ready`.
    if (payload['type'] !== 'auth' && !this.authenticated) {
      return;
    }

    this.socket.send(JSON.stringify(payload));
  }

  private disconnect(): void {
    this.clearReconnect();

    const socket = this.socket;

    this.socket = null;
    this.authenticated = false;
    socket?.close();
    this.setState('offline');
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null || getSession().status !== 'authenticated') {
      return;
    }

    const delay = Math.min(RECONNECT_MAX, RECONNECT_MIN * 2 ** this.attempts);

    this.attempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private setState(state: ConnectionState): void {
    if (this.state === state) {
      return;
    }

    this.state = state;

    for (const listener of this.stateListeners) {
      listener(state);
    }
  }
}

export const realtime = new RealtimeClient();
